export const ASSESSMENT_VERSION = 1;

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

export const RETIRED_QUESTION_IDS = deepFreeze([
  'review.interview-windows',
  'operations.useful-action',
  'review.planned-leave',
]);

const option = (id, label) => ({ id, label });

export const CAPABILITY_ANCHORS = deepFreeze([
  option('not-proved-yet', 'Not proved yet'),
  option('supported-it', 'Supported it'),
  option('owned-in-my-venue', 'Owned it in my venue'),
  option('led-beyond-one-venue', 'Led it beyond one venue'),
].map((anchor, index) => ({
  ...anchor,
  description: [
    'I do not yet have a real example of doing this.',
    'I have helped somebody else own this, but I have not carried it myself.',
    'I have personally led this to an outcome in my current or previous venue.',
    'I have repeatedly led this through other managers, teams or venues.',
  ][index],
})));

export const CAPABILITIES = deepFreeze([
  {
    id: 'operational-grip',
    label: 'Create operational grip',
    statement:
      'I can enter a venue, establish what matters, agree a clear plan and leave somebody else owning the actions.',
  },
  {
    id: 'lead-through-gm',
    label: 'Lead through a GM',
    statement:
      'I can improve standards through a General Manager and their team without taking over their job.',
  },
  {
    id: 'people-issues',
    label: 'Handle people issues',
    statement:
      'I can deal with underperformance or harmful behaviour promptly, fairly and without avoiding the uncomfortable conversation.',
  },
  {
    id: 'commercial-information',
    label: 'Use commercial information',
    statement:
      'I can read sales, wage percentage, stock levels and footfall indicators and decide what needs attention.',
  },
  {
    id: 'prioritise-patch',
    label: 'Prioritise a patch',
    statement:
      'I can keep several venues moving when more than one issue feels urgent.',
  },
  {
    id: 'communicate-close-loops',
    label: 'Communicate and close loops',
    statement:
      'I communicate early, clearly and to the right people, then check that the outcome happened.',
  },
  {
    id: 'challenge-standards-decisions',
    label: 'Challenge standards and decisions',
    statement:
      'I can challenge poor standards or a weak decision, including with somebody senior, without creating unnecessary drama.',
  },
  {
    id: 'own-outcome',
    label: 'Own the outcome',
    statement:
      'I stay accountable for the result when I delegate work to a manager or team member I supervise, when a supplier or contractor lets us down, or when the first plan does not work.',
  },
]);

export const ROLE_COMMITMENTS = deepFreeze([
  {
    id: 'nights-weekends-short-notice',
    statement:
      'I can remain available and make sound decisions when several venues need support outside normal hours, while helping the responsible GM own the solution rather than taking over.',
  },
  {
    id: 'travel-and-change-plan',
    statement:
      'I can travel between venues and change my planned day when safety, people or trading priorities require it.',
  },
  {
    id: 'difficult-conversations',
    statement:
      "I will hold difficult standards, conduct and performance conversations promptly rather than letting them become somebody else's problem.",
  },
  {
    id: 'patch-accountability',
    statement:
      'I accept accountability for results across my patch, including work I delegate to others.',
  },
  {
    id: 'step-back-from-own-venue',
    statement:
      'I can step back from personally running my own venue and allow others to lead it, even when their style is different from mine.',
  },
  {
    id: 'acting-period-outcome',
    statement:
      'I understand that this is an acting and training period, and that the outcome may include returning to a General Manager role.',
  },
]);

export const COMMITMENT_OPTIONS = deepFreeze([
  option('yes', 'Yes, I can commit to this.'),
  option('constraint', 'Yes, with a practical constraint we should understand.'),
  option('no', 'No, I cannot currently commit to this.'),
]);

export const SCENARIO_CHOICES = deepFreeze({
  fridayCollision: [
    option(
      'oj-a-01',
      "Call EINSTEIN's GM, establish the licensing and safety facts, require the log and escalation procedure to be followed, and decide whether your attendance is needed.",
    ),
    option(
      'oj-a-02',
      "Ask OLD BANK's duty manager for the exact kitchen gap and trading plan, then assign a named manager to coordinate available cover and a reduced-service fallback.",
    ),
    option('oj-a-03', 'Drive immediately to OLD BANK and work in the kitchen yourself.'),
    option(
      'oj-a-04',
      "Tell LAGO's GM that the commercial review will be scheduled after live trading and compliance risks are controlled, and request the numbers needed for that review.",
    ),
    option('oj-a-05', 'Send one group message asking all three venues to keep you updated.'),
    option(
      'oj-a-06',
      'Call the Area Manager with verified facts, your priorities and any support or escalation required.',
    ),
    option(
      'oj-a-07',
      "Tell EINSTEIN's GM to email a summary tomorrow because the officer is already on site.",
    ),
    option(
      'oj-a-08',
      "Visit LAGO first because poor sales and wage percentage affect the week's result.",
    ),
  ],
  repeatedStandards: [
    option('oj-b-01', 'Take the action list away and complete the urgent items yourself.'),
    option(
      'oj-b-02',
      'Ask the GM to forward all correspondence, in any form, so you can establish who is to blame.',
    ),
    option(
      'oj-b-03',
      'Separate immediate safety or trading risks from the repeated management failures, agree named owners and deadlines, and make clear that the GM remains accountable for the outcome.',
    ),
    option(
      'oj-b-04',
      'Send the list directly to the maintenance team and personally chase the actions until they are resolved.',
    ),
  ],
  resultsWithoutLeadership: [
    option('oj-c-01', 'Leave the GM alone while the commercial results remain strong.'),
    option(
      'oj-c-02',
      'Move the deputy to another venue and avoid disrupting the current results.',
    ),
    option(
      'oj-c-03',
      'Protect people, establish the facts promptly, set a clear behavioural boundary and follow the correct people process while keeping the venue controlled.',
    ),
    option(
      'oj-c-04',
      'Tell the GM to be kinder and review the situation at the end of the quarter.',
    ),
  ],
  dependencyDiagnosis: [
    option('oj-d-01', 'The patch needs a harder-working Operations Manager.'),
    option(
      'oj-d-02',
      'The GMs are not ready, so you should keep control until they improve.',
    ),
    option(
      'oj-d-03',
      'You have created dependency and need to reset decision rights, coaching, follow-up and escalation rules.',
    ),
    option('oj-d-04', 'The patch is too large and should immediately be reduced.'),
  ],
  seriousIncident: [
    option(
      'co-a-01',
      'Preserve evidence, make the situation safe, ensure the venue follows the correct incident and licensing process, escalate with verified facts, then maintain an action log.',
    ),
    option(
      'co-a-02',
      'Finish service, ask for an email and investigate at the next visit.',
    ),
    option(
      'co-a-03',
      'Post in the manager group so somebody with more experience can decide.',
    ),
    option(
      'co-a-04',
      'Close the venue immediately in every case and work out the facts later.',
    ),
  ],
  labourServiceFactors: [
    option('co-c-01', 'Deployment by trading period and role.'),
    option('co-c-02', 'Forecast quality and sales mix.'),
    option('co-c-03', 'Skills and productivity on each shift.'),
    option(
      'co-c-04',
      'Breaks, vacancies, absence and manager coverage.',
    ),
    option(
      'co-c-05',
      'Only the final weekly percentage, because the target was met.',
    ),
  ],
});

export const FICTIONAL_VENUES = deepFreeze({
  'VENUE A': {
    salesActual: 39_200,
    salesBudget: 42_000,
    likeForLikeSales: -3,
    labourActual: 31.8,
    labourTarget: 29,
    grossProfitActual: 68.2,
    grossProfitTarget: 70,
    guestComplaints: 7,
    voids: 1.9,
    teamTurnover: 18,
    venueHealthCheck: 'PASS',
  },
  'VENUE B': {
    salesActual: 52_600,
    salesBudget: 50_000,
    likeForLikeSales: 7,
    labourActual: 27.4,
    labourTarget: 28,
    grossProfitActual: 66.1,
    grossProfitTarget: 70,
    guestComplaints: 11,
    voids: 3.8,
    teamTurnover: 42,
    venueHealthCheck: 'PASS',
  },
  'VENUE C': {
    salesActual: 31_400,
    salesBudget: 34_000,
    likeForLikeSales: -9,
    labourActual: 36.5,
    labourTarget: 30,
    grossProfitActual: 71,
    grossProfitTarget: 70.5,
    guestComplaints: 3,
    voids: 0.7,
    teamTurnover: 12,
    venueHealthCheck: 'FAIL',
  },
});

export const SUPPORT_PRIORITIES = deepFreeze([
  option('lead-through-gms', 'Moving from doing to leading through GMs.'),
  option('commercial-diagnosis', 'Commercial diagnosis and action planning.'),
  option('people-process', 'People process and difficult conversations.'),
  option(
    'licensing-safety',
    'Licensing, safety and serious-incident escalation.',
  ),
  option('patch-planning', 'Planning and prioritising a venue patch.'),
  option('peer-accountability', 'Holding peers and former peers accountable.'),
  option('communicating-upwards', 'Communicating upwards with concise evidence.'),
  option('no-specific-priority', 'No specific priority identified yet.'),
]);

export const DECLARATIONS = deepFreeze([
  {
    id: 'own-honest-answers',
    statement: 'These answers are my own and are honest to the best of my knowledge.',
  },
  {
    id: 'no-guaranteed-appointment',
    statement: 'I understand that invitation and assessment do not guarantee appointment.',
  },
  {
    id: 'answers-final',
    statement: 'I understand that submitted answers are final.',
  },
  {
    id: 'acting-period-and-return',
    statement:
      'I understand the acting period, the 1 September appointment date and the possibility of returning to a General Manager role.',
  },
  {
    id: 'authority-and-responsibility',
    statement:
      'I understand that Operations Manager authority brings responsibility for standards, people and results across several venues.',
  },
]);

const q = (sectionId, id, type, prompt, rules = {}) => ({
  id,
  sectionId,
  type,
  prompt,
  required: true,
  ...rules,
});

const textQuestion = (sectionId, id, prompt, minLength, maxLength, rules = {}) =>
  q(sectionId, id, 'text', prompt, { minLength, maxLength, ...rules });

const sections = [
  {
    id: 'introduction',
    number: 1,
    title: 'Programme introduction',
    estimateMinutes: 3,
    eyebrow: 'OPERATIONS LEADERSHIP PROGRAMME',
    heading: "You've been chosen for a reason.",
    content: {
      opening:
        'This invitation means your work has been noticed. We believe you may be ready to lead beyond one venue, and we want to give you a proper opportunity to show us.',
      opportunity:
        'This is an Operations Manager role with real authority. The first patch is deliberately smaller, giving you room to learn the job while doing the job. From 20 July to 31 August, successful candidates will work through an acting and training period. Formal appointments begin on 1 September and run for an initial year.',
      reality:
        'The role is not a new badge for doing the same job. You will be expected to lead General Managers, improve several venues and create ownership in other people. Becoming the emergency GM for every venue is not the plan.',
      approach:
        'We are not looking for perfect interview answers. Specific truth beats polished waffle. Tell us what you have proved, where you still need help and what you would actually do when things become difficult. There are no bonus points for writing "email sent" and quietly moving the problem into somebody else\'s inbox.',
      ownership: {
        title: 'What we mean by ownership',
        body:
          'Owning a problem means remaining accountable until there is a verified outcome. It does not mean completing every task yourself. You may delegate actions to the appropriate people, but you must set clear responsibilities and deadlines, monitor progress, remove or escalate blockers, and confirm that the issue has genuinely been resolved.',
      },
      processFacts: [
        '75-90 minutes in total.',
        'Work saves automatically and can be resumed with the same invitation code.',
        'There is no recorded video.',
        'Submitted answers cannot be changed.',
        'Submission is followed by evidence review, a short remote interview and, for selected candidates, the acting trial.',
      ],
      primaryAction: 'Start with an honest baseline',
    },
    questions: [
      q(
        'introduction',
        'introduction.acknowledged',
        'acknowledgement',
        'I have read how the programme and assessment work and I am ready to begin.',
      ),
    ],
  },
  {
    id: 'starting-point',
    number: 2,
    title: 'Your starting point',
    estimateMinutes: 12,
    purpose:
      'This is a confidence-and-evidence calibration, not a self-awarded score. Rate each capability against what you can prove today.',
    questions: [
      q(
        'starting-point',
        'starting.capabilities',
        'capability-grid',
        'Rate each capability using the same four evidence anchors.',
        {
          capabilities: CAPABILITIES,
          options: CAPABILITY_ANCHORS,
          highestRatingId: 'led-beyond-one-venue',
        },
      ),
      q(
        'starting-point',
        'starting.strongest-evidence',
        'evidence-pair',
        'Choose the two capabilities where you have the strongest real evidence. For each one, describe a specific example: what was happening, what you personally did, what changed as a result, and how someone involved would describe your contribution.',
        { capabilities: CAPABILITIES, itemCount: 2, minLength: 120, maxLength: 1800 },
      ),
      q(
        'starting-point',
        'starting.development-areas',
        'evidence-pair',
        'Choose the two capabilities most likely to need coaching, practice or closer support in your first month. What is missing today, and what support would help you become dependable in that area?',
        { capabilities: CAPABILITIES, itemCount: 2, minLength: 80, maxLength: 1400 },
      ),
      textQuestion(
        'starting-point',
        'starting.personal-dependency',
        'What part of your current venue still depends too much on you personally? What have you tried to do about it, and what does that tell you about moving into a multi-venue role?',
        120,
        1600,
      ),
      textQuestion(
        'starting-point',
        'starting.recent-miss',
        'Tell us about one result, standard or people issue you did not handle as well as you should have. What part was yours to own, and what have you changed since?',
        120,
        1600,
      ),
    ],
  },
  {
    id: 'role-reality',
    number: 3,
    title: 'Role reality',
    estimateMinutes: 6,
    purpose:
      'This section asks for a practical commitment, not a soft acknowledgement. Honest constraints do not automatically reject a candidate.',
    questions: [
      q(
        'role-reality',
        'reality.commitments',
        'commitment-grid',
        'Tell us what you can commit to in practice.',
        {
          commitments: ROLE_COMMITMENTS,
          options: COMMITMENT_OPTIONS,
          explanationMinLength: 40,
          explanationMaxLength: 600,
        },
      ),
      textQuestion(
        'role-reality',
        'reality.stretching-part',
        'Which part of the role will stretch you most in practice, and what will you put in place during the first two weeks so it does not become an excuse?',
        100,
        1400,
      ),
      textQuestion(
        'role-reality',
        'reality.practical-constraints',
        'Is there anything about your availability, travel, health, caring responsibilities or current venue handover that we should understand before considering an acting appointment? Share only what is relevant to making the role workable.',
        0,
        1200,
        { required: false, requiredWhenCommitmentHasConstraint: true },
      ),
    ],
  },
  {
    id: 'motivation-track-record',
    number: 4,
    title: 'Motivation and track record',
    estimateMinutes: 10,
    opening:
      'You did not find this role on a jobs board; we invited you. What matters now is what you want to do with the opportunity, and whether the work itself motivates you after the novelty wears off.',
    questions: [
      textQuestion(
        'motivation-track-record',
        'motivation.work',
        'What do you want to build, improve or become capable of during this Operations Manager year?',
        100,
        1500,
        { label: 'The work you want to do' },
      ),
      textQuestion(
        'motivation-track-record',
        'motivation.attractive-difficult',
        'What genuinely attracts you to the role? What part do you expect to enjoy least, and why are you willing to take that on anyway?',
        120,
        1800,
        { label: 'The attractive part and the difficult part' },
      ),
      textQuestion(
        'motivation-track-record',
        'motivation.result-moved',
        'Give us one result or standard you materially improved. Set out the starting point, your decisions and actions, the measurable result, and how we could verify it.',
        160,
        2000,
        { label: 'A result you moved' },
      ),
      textQuestion(
        'motivation-track-record',
        'motivation.failed-plan',
        'Tell us about a plan of yours that did not work. When did you realise, what did you own, who did you communicate with and what did you change?',
        140,
        1800,
        { label: 'A plan that failed' },
      ),
      textQuestion(
        'motivation-track-record',
        'motivation.honest-risk',
        'What behaviour or habit of yours could make us regret appointing you if you did not manage it properly?',
        80,
        1200,
        { label: 'The honest risk' },
      ),
      textQuestion(
        'motivation-track-record',
        'motivation.worthwhile-year',
        'If the year ended with you returning to a General Manager role, what would still make the experience worthwhile for you and useful to Pub Invest?',
        80,
        1200,
        { label: 'A worthwhile year' },
      ),
    ],
  },
  {
    id: 'operational-judgement',
    number: 5,
    title: 'Operational judgement',
    estimateMinutes: 18,
    responsePattern:
      'Say what you do first, what you delegate and to whom, who needs communication and by when, what you will check rather than assume, and what follow-up will prevent the issue from returning.',
    questions: [
      q(
        'operational-judgement',
        'judgement.friday-rank',
        'rank',
        'Rank the first four moves.',
        {
          scenario:
            "It is Friday at 18:20. OLD BANK has lost two chefs for an 80-cover evening, and its duty manager says service may fail. At EINSTEIN, a licensing officer has arrived following concerns about noise and an incomplete door log; the GM is present but flustered. LAGO's flash report shows sales 11% below plan and wage percentage at 34%, and its GM wants you to come over immediately. You are 25 minutes from OLD BANK, 35 minutes from EINSTEIN and 20 minutes from LAGO.",
          options: SCENARIO_CHOICES.fridayCollision,
          rankCount: 4,
        },
      ),
      textQuestion(
        'operational-judgement',
        'judgement.friday-plan',
        'Talk us through your first 30 minutes and the rest of the evening. Be clear about ownership, communication and what you will personally verify.',
        180,
        2400,
      ),
      q(
        'operational-judgement',
        'judgement.repeated-standards-choice',
        'single-choice',
        'Choose the strongest opening move.',
        {
          scenario:
            "A venue scores low on a standards visit. Four actions also appeared on the previous visit. The GM says the maintenance team has been nudged twice about the outstanding jobs on BOSS, but they have not replied. Team members say they do not know who owns the actions. The GM believes the problems are now other people's responsibility.",
          options: SCENARIO_CHOICES.repeatedStandards,
        },
      ),
      textQuestion(
        'operational-judgement',
        'judgement.repeated-standards-plan',
        'Write the first part of the conversation you would have with the GM, then set out the 48-hour recovery plan and the checks you would make over the following two weeks.',
        180,
        2400,
      ),
      q(
        'operational-judgement',
        'judgement.people-risk-choice',
        'single-choice',
        'Choose the strongest first position.',
        {
          scenario:
            'A GM is delivering strong sales and wage percentage results. Staff turnover is above the norm, two team members have independently described being humiliated in front of colleagues, and a respected deputy has resigned. The GM says high standards are not for everyone and that the numbers prove their approach works.',
          options: SCENARIO_CHOICES.resultsWithoutLeadership,
        },
      ),
      textQuestion(
        'operational-judgement',
        'judgement.people-risk-plan',
        'What do you do on the first day, what do you avoid assuming, and what evidence would tell you over 30 days that the situation is genuinely improving?',
        160,
        2200,
      ),
      q(
        'operational-judgement',
        'judgement.dependency-choice',
        'single-choice',
        'Choose the most accurate diagnosis.',
        {
          scenario:
            'Three weeks into the acting role, GMs call you for routine decisions, your former venue still contacts you directly, and you are personally closing actions after every visit. Standards are holding, but your week is full and very little moves unless you touch it.',
          options: SCENARIO_CHOICES.dependencyDiagnosis,
        },
      ),
      textQuestion(
        'operational-judgement',
        'judgement.dependency-plan',
        'How would you reset the next seven days without dropping standards? Include what you would stop doing, what each GM would own, your contact rhythm and how you would know the change is working.',
        160,
        2200,
      ),
    ],
  },
  {
    id: 'company-operations',
    number: 6,
    title: 'Company and operations',
    estimateMinutes: 8,
    purpose:
      'This section tests applied operating judgement. Answers should be consistent with current Pub Invest procedures, licensing duties and people processes.',
    questions: [
      q(
        'company-operations',
        'operations.serious-incident',
        'single-choice',
        "A serious incident may affect guest or staff safety and the venue's licence. Which sequence is strongest?",
        { options: SCENARIO_CHOICES.seriousIncident },
      ),
      textQuestion(
        'company-operations',
        'operations.action-rewrite',
        'Rewrite "Improve cellar standards as soon as possible" as a clear action. Include who owns it, what must happen, when it must be completed and how the outcome will be checked.',
        60,
        600,
        { label: 'From intention to accountable action' },
      ),
      q(
        'company-operations',
        'operations.labour-service-factors',
        'multi-choice',
        'A venue is on its weekly wage percentage target but service is repeatedly poor at peak times. What do you examine before deciding the answer?',
        { options: SCENARIO_CHOICES.labourServiceFactors, minItems: 1 },
      ),
      textQuestion(
        'company-operations',
        'operations.stock-variance',
        'A GM says a poor stock result is "probably deliveries." What evidence and control checks do you expect before accepting or rejecting that explanation?',
        100,
        1500,
      ),
      textQuestion(
        'company-operations',
        'operations.visit-leaves-behind',
        'What should be different after a strong Operations Manager visit, and what should the GM own after you leave?',
        100,
        1500,
      ),
      textQuestion(
        'company-operations',
        'operations.knowledge-gap',
        'Name one Pub Invest process, control or area of policy you understand well enough to coach now. Then name one you would need to learn more deeply before carrying full Operations Manager responsibility.',
        120,
        1800,
      ),
    ],
  },
  {
    id: 'people-leadership',
    number: 7,
    title: 'People leadership',
    estimateMinutes: 12,
    questions: [
      textQuestion(
        'people-leadership',
        'people.underperforming-gm',
        'A long-serving GM is well liked and loyal, but standards and results have missed expectation for three months. Previous conversations were friendly, vague and changed little. How do you prepare, open the conversation, set the required change and support them without lowering the standard?',
        180,
        2200,
      ),
      textQuestion(
        'people-leadership',
        'people.challenge-upwards',
        'You believe an instruction from a more senior leader creates a serious risk to people, licensing or commercial performance. How do you challenge it? What do you do if, after hearing your evidence, they keep the decision?',
        140,
        1800,
      ),
      textQuestion(
        'people-leadership',
        'people.build-independence',
        'A capable new GM asks you to approve routine decisions every day. Show us how you would coach them over four weeks so they become more independent without feeling abandoned.',
        160,
        2000,
      ),
      textQuestion(
        'people-leadership',
        'people.written-message',
        'A GM has missed a clearly agreed standards deadline. Safety is not immediately at risk, but the failure is repeated and they have not contacted you. Write the message you would send now. It should be clear enough to create action without trying to conduct the whole conversation by text.',
        60,
        900,
      ),
      textQuestion(
        'people-leadership',
        'people.pressure-impact',
        'Pressure is part of the role. Which part of your leadership is most likely to slip when your workload builds? Give a real example, then explain what you do to prevent that pressure from affecting your GMs, standards or results.',
        80,
        1200,
      ),
    ],
  },
  {
    id: 'commercial-thinking',
    number: 8,
    title: 'Commercial thinking',
    estimateMinutes: 15,
    dataPack: FICTIONAL_VENUES,
    glossary: {
      likeForLike: 'Sales compared with the same venue and trading period last year.',
      grossProfit: 'The percentage of sales left after the direct cost of products sold.',
      voids: 'The percentage of recorded sales cancelled or removed from bills.',
      rollingTurnover: 'The share of staff members who left during the latest 12 months.',
    },
    questions: [
      ...[1, 2, 3].map((number) =>
        textQuestion(
          'commercial-thinking',
          `commercial.insight-${number}`,
          'Give us the three most important insights in this data. Use figures, explain why each matters and distinguish a symptom from a likely cause.',
          80,
          700,
          { label: `Insight ${number}`, group: 'commercial.patch-insights' },
        ),
      ),
      textQuestion(
        'commercial-thinking',
        'commercial.first-visit',
        'You have time for one full venue visit tomorrow. Which venue do you visit first, what is your working hypothesis and what could make you change that priority?',
        140,
        1800,
      ),
      textQuestion(
        'commercial-thinking',
        'commercial.missing-information',
        'What information is missing from the pack that you would request before reaching a firm conclusion? Choose only what could materially change your decision.',
        100,
        1400,
      ),
      textQuestion(
        'commercial-thinking',
        'commercial.seven-day-plan',
        'Set out your seven-day plan: actions, owners and the checks you expect. Then name the measures you would review at 30 days.',
        180,
        2200,
      ),
      textQuestion(
        'commercial-thinking',
        'commercial.leadership-update',
        'Write a short update to the Area Manager. Summarise the patch, your first priority, the action underway and any support or decision you need. Maximum 140 words.',
        50,
        1100,
        { maxWords: 140 },
      ),
    ],
  },
  {
    id: 'review-submit',
    number: 9,
    title: 'Review and submit',
    estimateMinutes: 5,
    questions: [
      textQuestion(
        'review-submit',
        'review.acting-date',
        'Enter the earliest date you would be available to begin acting duties. Use any clear format, for example 20 July.',
        2,
        100,
        { label: 'Earliest date available for acting duties' },
      ),
      textQuestion(
        'review-submit',
        'review.planned-leave-details',
        'List any planned leave before 1 September, including the dates. If you have none, write "None".',
        2,
        400,
        { label: 'Planned leave before 1 September' },
      ),
      textQuestion(
        'review-submit',
        'review.travel-rota-constraints',
        'Travel or rota constraints not already explained',
        0,
        800,
        { required: false },
      ),
      q(
        'review-submit',
        'review.support-priorities',
        'support-priorities',
        'Select up to three areas where focused support would help you become effective faster. This is not a weakness contest; it helps us plan the trial properly.',
        {
          options: SUPPORT_PRIORITIES,
          minItems: 1,
          maxItems: 3,
          exclusiveOptionId: 'no-specific-priority',
        },
      ),
      textQuestion(
        'review-submit',
        'review.final-open-question',
        'Is there anything else that could affect your acting period or the support you would need to succeed? Tell us now so we can plan properly.',
        0,
        1200,
        { required: false },
      ),
      q(
        'review-submit',
        'review.declarations',
        'declarations',
        'Confirm each declaration before submitting.',
        { declarations: DECLARATIONS },
      ),
    ],
  },
];

export const ASSESSMENT_SECTIONS = deepFreeze(sections);

export const QUESTION_INDEX = deepFreeze(
  Object.fromEntries(
    ASSESSMENT_SECTIONS.flatMap(({ questions }) =>
      questions.map((question) => [question.id, question]),
    ),
  ),
);
